import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, Key, Globe, Gift, Music, Upload } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "/wordmark-tight.png";
const APP_ICON_URL = "/sidebar-logo.png";

// Country list
const COUNTRIES = [
  "United States", "Canada", "United Kingdom", "Australia", "Germany", "France", 
  "Japan", "South Korea", "Brazil", "Mexico", "India", "China", "Spain", "Italy",
  "Netherlands", "Sweden", "Norway", "Denmark", "Finland", "Belgium", "Switzerland",
  "Austria", "Poland", "Portugal", "Ireland", "New Zealand", "Singapore", "South Africa",
  "Argentina", "Chile", "Colombia", "Philippines", "Indonesia", "Thailand", "Vietnam",
  "Malaysia", "Nigeria", "Kenya", "Egypt", "Israel", "Turkey", "Russia", "Ukraine",
  "Czech Republic", "Romania", "Hungary", "Greece", "Other"
].sort();

// Tiny app icon component for auth pages
const TinyAppIcon = () => (
  <div className="fixed bottom-6 right-6 z-50">
    <img 
      src={APP_ICON_URL} 
      alt="AUDIVUE" 
      className="w-10 h-10 rounded-xl shadow-xl shadow-purple-500/30 border border-white/10"
    />
  </div>
);

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      // Resume where the user came from: ?next= takes priority, then invite,
      // then home. Defaults to /, never breaks if next is missing.
      const nextUrl = searchParams.get('next');
      if (nextUrl && nextUrl.startsWith('/')) {
        navigate(nextUrl);
      } else if (inviteCode) {
        navigate(`/j/${inviteCode}`);
      } else {
        navigate('/');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#050505' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src={LOGO_URL} alt="AUDIVUE" className="h-16 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-2">Welcome Back</h1>
          <p className="text-zinc-400 font-body">Sign in to continue to AUDIVUE</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-zinc-300">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-white focus:ring-white/20"
                data-testid="login-email-input"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-zinc-300">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="pl-10 pr-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-white focus:ring-white/20"
                data-testid="login-password-input"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-white hover:bg-zinc-200 text-black font-semibold py-3 rounded-full"
            data-testid="login-submit-btn"
          >
            {loading ? 'Signing in...' : 'Sign In'}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </form>

        <div className="text-center mt-4">
          <Link to="/forgot-password" className="text-zinc-400 hover:text-white text-sm" data-testid="forgot-password-link">
            Forgot your password?
          </Link>
        </div>

        <p className="text-center mt-6 text-zinc-400">
          Don't have an account?{' '}
          <Link to="/register" className="text-white hover:underline font-medium" data-testid="register-link">
            Sign Up
          </Link>
        </p>
      </div>
      <TinyAppIcon />
    </div>
  );
};

export const RegisterPage = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('');
  // Generic signup: everyone joins as a listener. Role auto-promotes to
  // "artist" on first upload — no need to ask the user to self-classify.
  const role = 'listener';
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { register, axiosInstance } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      toast.error('Please agree to the Terms of Service');
      return;
    }
    if (!country) {
      toast.error('Please select your country');
      return;
    }
    setLoading(true);
    try {
      await register(username, email, password, country, role);
      toast.success("Account created!");
      // Preserve the `next` URL across the profile-setup detour so the user
      // lands back where they came from (e.g. the video they were about to
      // save) after they finish setup. sessionStorage survives the redirect
      // and is cleared by ProfileSetupPage on completion.
      const nextUrl = searchParams.get('next');
      if (nextUrl && nextUrl.startsWith('/')) {
        try { sessionStorage.setItem('aivue.postAuthNext', nextUrl); } catch { /* noop */ }
      }
      navigate('/profile-setup');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#050505' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src={LOGO_URL} alt="AUDIVUE" className="h-16 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-2">Create Account</h1>
          <p className="text-zinc-400 font-body">MUSIC VIDEOS, REBORN</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="username" className="text-zinc-300">Username</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Choose a username"
                className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-white focus:ring-white/20"
                data-testid="register-username-input"
                required
                minLength={3}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-zinc-300">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-white focus:ring-white/20"
                data-testid="register-email-input"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-zinc-300">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                className="pl-10 pr-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-white focus:ring-white/20"
                data-testid="register-password-input"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="country" className="text-zinc-300">Country</Label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500 z-10 pointer-events-none" />
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger 
                  className="pl-10 bg-zinc-900 border-zinc-800 text-white focus:border-white focus:ring-white/20"
                  data-testid="register-country-select"
                >
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                  {COUNTRIES.map((c) => (
                    <SelectItem 
                      key={c} 
                      value={c}
                      className="text-white hover:bg-zinc-800 focus:bg-zinc-800"
                    >
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Role selector removed — generic signup. Anyone who later uploads
              a video is auto-promoted to "artist" on the backend. */}

          {/* Terms of Service agreement — required */}
          <label
            className="flex items-start gap-3 px-1 cursor-pointer select-none"
            data-testid="register-terms-label"
          >
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-0.5 w-4 h-4 flex-shrink-0 accent-orange-500 cursor-pointer"
              data-testid="register-terms-checkbox"
            />
            <span className="text-sm text-zinc-300 leading-snug">
              I have read and agree to the{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-400 hover:text-orange-300 underline"
                data-testid="register-terms-link"
              >
                Terms of Service
              </a>
            </span>
          </label>

          <Button
            type="submit"
            disabled={loading || !agreedToTerms}
            className="w-full bg-white hover:bg-zinc-200 text-black font-semibold py-3 rounded-full disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="register-submit-btn"
          >
            {loading ? 'Creating account...' : 'Create Account'}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </form>

        <p className="text-center mt-8 text-zinc-400">
          Already have an account?{' '}
          <Link to="/login" className="text-white hover:underline font-medium" data-testid="login-link">
            Sign In
          </Link>
        </p>
      </div>
      <TinyAppIcon />
    </div>
  );
};

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetLink, setResetLink] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSent(true);
        // If email couldn't be sent, show the reset link for testing
        if (data.reset_link) {
          setResetLink(data.reset_link);
          toast.success('Reset link generated (testing mode)');
        } else {
          toast.success('Check your email for reset instructions');
        }
      } else {
        toast.error(data.detail || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#050505' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src={LOGO_URL} alt="AUDIVUE" className="h-16 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-2">Reset Password</h1>
          <p className="text-zinc-400 font-body">
            {sent ? 'Reset link ready' : 'Enter your email to receive a reset link'}
          </p>
        </div>

        {sent ? (
          <div className="text-center space-y-6">
            <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-green-400" />
            </div>
            
            {resetLink ? (
              <>
                <p className="text-zinc-300">
                  Click the button below to reset your password:
                </p>
                <Link to={resetLink.replace(process.env.REACT_APP_BACKEND_URL, '')}>
                  <Button className="bg-white hover:bg-zinc-200 text-black font-semibold rounded-full">
                    Reset Password Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <p className="text-zinc-600 text-xs">
                  (Testing mode - email delivery requires domain verification)
                </p>
              </>
            ) : (
              <>
                <p className="text-zinc-300">
                  If an account exists for <span className="text-white font-medium">{email}</span>, you'll receive an email with instructions to reset your password.
                </p>
                <p className="text-zinc-500 text-sm">
                  Didn't receive the email? Check your spam folder or try again.
                </p>
              </>
            )}
            
            <Button
              onClick={() => { setSent(false); setResetLink(null); }}
              variant="outline"
              className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-full"
            >
              Try another email
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-white focus:ring-white/20"
                  data-testid="forgot-email-input"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-white hover:bg-zinc-200 text-black font-semibold py-3 rounded-full"
              data-testid="forgot-submit-btn"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </form>
        )}

        <p className="text-center mt-8 text-zinc-400">
          Remember your password?{' '}
          <Link to="/login" className="text-white hover:underline font-medium">
            Sign In
          </Link>
        </p>
      </div>
      <TinyAppIcon />
    </div>
  );
};

export const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  
  // Get token from URL
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        toast.success('Password reset successfully!');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (err) {
      toast.error('Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#050505' }}>
        <div className="w-full max-w-md text-center">
          <img src={LOGO_URL} alt="AUDIVUE" className="h-16 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white mb-4">Invalid Reset Link</h1>
          <p className="text-zinc-400 mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password">
            <Button className="bg-white hover:bg-zinc-200 text-black font-semibold rounded-full">
              Request New Link
            </Button>
          </Link>
        </div>
        <TinyAppIcon />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#050505' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src={LOGO_URL} alt="AUDIVUE" className="h-16 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-2">New Password</h1>
          <p className="text-zinc-400 font-body">Create a new password for your account</p>
        </div>

        {success ? (
          <div className="text-center space-y-6">
            <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-green-400" />
            </div>
            <p className="text-zinc-300">Your password has been reset successfully!</p>
            <Button
              onClick={() => navigate('/login')}
              className="bg-white hover:bg-zinc-200 text-black font-semibold rounded-full"
            >
              Sign In Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  className="pl-10 pr-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-white focus:ring-white/20"
                  data-testid="reset-password-input"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-zinc-300">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-white focus:ring-white/20"
                  data-testid="reset-confirm-password-input"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-white hover:bg-zinc-200 text-black font-semibold py-3 rounded-full"
              data-testid="reset-submit-btn"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </form>
        )}

        <p className="text-center mt-8 text-zinc-400">
          Remember your password?{' '}
          <Link to="/login" className="text-white hover:underline font-medium">
            Sign In
          </Link>
        </p>
      </div>
      <TinyAppIcon />
    </div>
  );
};
